import {
  Box,
  Text,
  ModalContent,
  Tfoot,
  Tr,
  Td,
  ModalHeader,
  Flex,
  Modal,
  ModalOverlay,
  ModalBody,
  Spinner,
  ModalCloseButton,
} from "@chakra-ui/react";
import { useContext, useEffect, useMemo, useRef, useState } from "react";
import { useLoanTransactions } from "hooks/loan-transactions/useLoanTransactions";
import { useCustomFormats } from "hooks/useCustomFormat";
import { TableTransactions } from "../components/TableTransactions";
import { BankingInformation } from "./components/BankingInformation";
import { useStatement } from "hooks/statements/useStatement";
import jsPDF from "jspdf";
import { ActionButtons } from "../components/ActionButtons";
import { LoanContext } from "providers/loan";
import { Header } from "../components/header";
import { StatementEntryItemType } from "@common/loans/statements/amortization.view-model";
import moment from "moment";

interface RedemptionStatementModalProps {
  onClose: () => void;
  isOpen: boolean;
  lastStatementDate: string;
}

export function RedemptionStatementModal({
  onClose,
  isOpen,
  lastStatementDate,
}: RedemptionStatementModalProps) {
  const { loan } = useContext(LoanContext);
  const { formatDate, formatCurrency } = useCustomFormats();
  const printRef = useRef<HTMLDivElement>(null);

  const { save: saveStatement, isSaving } = useStatement();

  const [pdf, setPdf] = useState<jsPDF>();

  const { data: statement, isLoading } = useLoanTransactions({
    loan,
    dateRange: {
      start: undefined,
      end: lastStatementDate,
    },
  });

  const lastDate = useMemo(() => {
    return formatDate(statement?.endAt ?? null);
  }, [statement, formatDate]);

  const fileName = useMemo(() => {
    return `Redemption Statement for ${formatDate(lastStatementDate).replace(/\//g, "-")}`;
  }, [formatDate, lastStatementDate]);

  useEffect(() => {
    if (loan?.id && !isLoading && !pdf) {
      const interval = setInterval(() => {
        if (printRef.current) {
          clearInterval(interval);
          saveStatement({
            element: printRef.current,
            name: fileName,
            loanId: loan.id,
          }).then(pdf => {
            setPdf(pdf);
          });
        }
      }, 100);
    }
  }, [loan?.id, isLoading, saveStatement, fileName, pdf]);

  if (!statement) return null;

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="6xl">
      <ModalOverlay />
      <ModalContent>
        <ModalCloseButton />
        <ModalHeader display="flex" alignItems="center">
          Redemption Statement
          <ActionButtons pdf={pdf} isSaving={isSaving} isLoading={isLoading} fileName={fileName} />
        </ModalHeader>
        <ModalBody onWheel={e => e.stopPropagation()} mb={3}>
          {isLoading || !loan ? (
            <Spinner />
          ) : (
            <Flex direction="column" gap={6} className="print-area" ref={printRef}>
              <Header name="Redemption Statement" statement={statement} />

              <Flex direction="column" gap={2}>
                <Flex
                  justifyContent={"space-between"}
                  alignItems={"center"}
                  fontWeight={"semibold"}
                >
                  <Text color={"black"}>
                    {`Amount Required to fully repay the loan on ${lastDate}`}
                  </Text>
                  <Flex
                    border={"1px solid black"}
                    color={"black"}
                    px={3}
                    py={1}
                    alignItems={"center"}
                    justifyContent={"center"}
                  >
                    {formatCurrency(statement?.remainingBalance ?? 0)}
                  </Flex>
                </Flex>
                <Box>
                  <TableTransactions
                    data={statement?.entries ?? []}
                    tfoot={
                      <Tfoot>
                        <Tr fontWeight={"semibold"}>
                          <Td border={0}></Td>
                          <Td border={0} py={8} colSpan={3}>
                            {`Amount required to repay loan in full on ${lastDate}, following redemption:`}
                          </Td>
                          <Td border={0} py={8} textAlign={"right"}>
                            {formatCurrency(statement?.remainingBalance ?? 0)}
                          </Td>
                        </Tr>
                      </Tfoot>
                    }
                  />
                  <hr />
                </Box>
              </Flex>

              <BankingInformation />
            </Flex>
          )}
        </ModalBody>
      </ModalContent>
    </Modal>
  );
}
